import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { fmt, printLedger } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Printer, Mail } from "lucide-react";
import { toast } from "sonner";
import { SendMailDialog, mailTargets } from "@/components/SendMail";

export default function PartyLedger() {
  const { id } = useParams();
  const [ledger, setLedger] = useState(null);
  const [mailFor, setMailFor] = useState(null);

  useEffect(() => {
    api.get(`/parties/${id}/ledger`).then((r) => setLedger(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  if (!ledger) return null;
  const p = ledger.party;
  const colour = (t) => (t.includes("Sales") || t.includes("Vendor Payment") ? "text-emerald-400" : "text-amber-400");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="party-ledger-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/parties" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="ledger-back-link"><ArrowLeft className="h-4 w-4" /> Parties</Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">{p.name}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <Badge variant="outline" className="capitalize text-sky-300 border-sky-500/30 mr-2">{p.party_type}</Badge>
            {p.gstin && <span className="font-mono-sku">GSTIN {p.gstin} · </span>}{p.state}{p.phone ? ` · ${p.phone}` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="mail-ledger-btn" onClick={() => setMailFor(mailTargets.ledger(ledger))} className="gap-2 border-border text-violet-300"><Mail className="h-4 w-4" /> Email Statement</Button>
          <Button variant="outline" data-testid="print-ledger-btn" onClick={() => printLedger(ledger)} className="gap-2 border-border"><Printer className="h-4 w-4" /> Print Statement</Button>
        </div>
      </div>
      <SendMailDialog target={mailFor} onClose={() => setMailFor(null)} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Opening</p><p className="text-xl font-extrabold font-mono-sku">{fmt(ledger.opening_balance)}</p></CardContent></Card>
        <Card className="bg-emerald-500/10 border-emerald-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Receivable</p><p className="text-xl font-extrabold text-emerald-400 font-mono-sku" data-testid="ledger-receivable">{fmt(p.receivable)}</p></CardContent></Card>
        <Card className="bg-amber-500/10 border-amber-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Payable</p><p className="text-xl font-extrabold text-amber-400 font-mono-sku" data-testid="ledger-payable">{fmt(p.payable)}</p></CardContent></Card>
        <Card className="bg-sky-500/10 border-sky-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Closing Balance</p><p className="text-xl font-extrabold text-sky-400 font-mono-sku" data-testid="ledger-closing">{fmt(ledger.closing_balance)}</p><p className="text-xs text-muted-foreground">{ledger.closing_balance >= 0 ? "They owe you" : "You owe them"}</p></CardContent></Card>
      </div>

      <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">Reference</th>
            <th className="px-4 py-3 font-medium text-right">Debit</th><th className="px-4 py-3 font-medium text-right">Credit</th><th className="px-4 py-3 font-medium text-right">Balance</th>
          </tr></thead>
          <tbody data-testid="ledger-table-body">
            <tr className="border-b border-border/50 text-muted-foreground"><td className="px-4 py-3" colSpan={5}>Opening Balance</td><td className="px-4 py-3 text-right font-mono-sku">{fmt(ledger.opening_balance)}</td></tr>
            {ledger.entries.map((e, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-white/5">
                <td className="px-4 py-3 font-mono-sku text-xs">{e.date}</td>
                <td className={`px-4 py-3 font-medium ${colour(e.type)}`}>{e.type}</td>
                <td className="px-4 py-3 font-mono-sku text-xs">{e.ref}</td>
                <td className="px-4 py-3 text-right font-mono-sku">{e.debit ? fmt(e.debit) : ""}</td>
                <td className="px-4 py-3 text-right font-mono-sku">{e.credit ? fmt(e.credit) : ""}</td>
                <td className="px-4 py-3 text-right font-mono-sku font-semibold">{fmt(e.balance)}</td>
              </tr>
            ))}
            {ledger.entries.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No transactions yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
